/**
 * The realtime tier's authentication contract (phase plan S2-4, architecture
 * §7 + §14 rule 17).
 *
 * Shared because both ends need it and neither may guess at the other's half:
 * `apps/game` produces these values in `BaseGameRoom.onAuth`, and `apps/web`
 * reads the rejection code off `room.onError` to decide what to tell the
 * player. No Colyseus import on either side of this file — `apps/web` may not
 * see `colyseus` or `@colyseus/schema` at all (LOCKED rule 17).
 */
/**
 * Why a socket was refused.
 *
 * **These are Colyseus *error* codes, not WebSocket close codes.** Colyseus
 * carries an application rejection as an `ERROR` message holding this number
 * and then closes the socket with its own `CloseCode.WITH_ERROR` (4002) — so
 * the number below is what `room.onError(code, message)` receives, and it is
 * the only thing that distinguishes "your token expired" from "you are
 * suspended". Do not expect to read them off `CloseEvent.code`.
 *
 * The range is chosen to collide with nothing: Colyseus's own `ErrorCode`
 * values are 520–526 and its `CloseCode` values are 4000–4010.
 */
export const ROOM_AUTH_ERROR = {
    /** No token on the socket at all. A client bug, or an unauthenticated probe. */
    MISSING_TOKEN: 4401,
    /** Signature, `exp`, `iss`, `aud` or `kid` did not check out. Includes expiry. */
    INVALID_TOKEN: 4402,
    /** The token verified, but no `players` row matches its subject. */
    UNKNOWN_PLAYER: 4403,
    /** `players.is_suspended`. Distinct on purpose — the UI must not say "log in again". */
    SUSPENDED: 4404,
    /** `players.deleted_at` is set. */
    DELETED: 4405,
    /** The room tier could not reach the API's JWKS or the database. Retryable. */
    AUTH_UNAVAILABLE: 4503,
};
/** True for a code the client should treat as "get a fresh token and retry". */
export function isRetryableAuthError(code) {
    return code === ROOM_AUTH_ERROR.INVALID_TOKEN || code === ROOM_AUTH_ERROR.AUTH_UNAVAILABLE;
}
/** The audience claim every WS access token must carry (architecture §7). */
export const WS_TOKEN_AUDIENCE = "wordy-royale-game";
//# sourceMappingURL=room-auth.js.map